"use client";

import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Send } from "lucide-react";
import {
  ContactPhoneIti,
  type ContactPhoneItiHandle,
} from "@/app/contact/ContactPhoneIti";

const ACCENT = "#2BA6E6";
const API_URL = "/api/account/data-deletion-request";

interface DeletionApiResponse {
  success: boolean;
  message?: string;
  data?: {
    requestId?: number;
    status?: string;
    matchedAccountFound?: boolean;
  };
}

type RequestType = "full_account" | "posts_only" | "listings_only" | "";

const REQUEST_OPTIONS: { value: RequestType; label: string }[] = [
  { value: "full_account", label: "Full account deletion" },
  { value: "posts_only", label: "Posts only" },
  { value: "listings_only", label: "Listings only" },
];

interface FormState {
  username: string;
  email: string;
  requestType: RequestType;
  reason: string;
}

const EMPTY: FormState = {
  username: "",
  email: "",
  requestType: "",
  reason: "",
};

function requestTypeLabel(value: RequestType): string {
  return REQUEST_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

export function AccountChangeRequestForm() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [identifierError, setIdentifierError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [hasPhoneValue, setHasPhoneValue] = useState(false);
  const phoneRef = useRef<ContactPhoneItiHandle>(null);
  const [submitted, setSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const inputClass = (hasError: boolean) =>
    `w-full px-4 py-3 rounded-xl border text-sm text-neutral-900 placeholder-neutral-400 outline-none transition-all duration-200 bg-white focus:ring-2 ${
      hasError
        ? "border-red-400 focus:ring-red-100"
        : "border-neutral-200 focus:border-[#2BA6E6] focus:ring-blue-50"
    }`;

  const validate = (): boolean => {
    const e: Partial<Record<keyof FormState, string>> = {};
    const phoneDigits = phoneRef.current?.getMobileDigits() ?? "";
    const hasUsername = !!form.username.trim();
    const hasEmail = !!form.email.trim();
    const hasPhone = phoneDigits.length > 0;

    setHasPhoneValue(hasPhone);

    if (!hasUsername && !hasEmail && !hasPhone) {
      setIdentifierError(
        "Please provide at least one of: user name, phone number, or email address."
      );
    } else {
      setIdentifierError(null);
    }

    if (hasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      e.email = "Please enter a valid email address.";
    }

    if (hasPhone) {
      if (!(phoneRef.current?.validate() ?? false)) {
        setPhoneError("Please enter a valid phone number.");
      } else {
        setPhoneError(null);
      }
    } else {
      setPhoneError(null);
    }

    if (!form.requestType) {
      e.requestType = "Please select what you would like to delete.";
    }

    if (!form.reason.trim()) {
      e.reason = "Please describe the reason for your request.";
    } else if (form.reason.trim().length < 10) {
      e.reason = "Please provide a bit more detail (at least 10 characters).";
    }

    setErrors(e);

    const hasIdentifier = hasUsername || hasEmail || hasPhone;
    const phoneOk = !hasPhone || (phoneRef.current?.validate() ?? false);

    return hasIdentifier && phoneOk && Object.keys(e).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormState]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
    if (name === "username" || name === "email") {
      if (identifierError) setIdentifierError(null);
    }
    if (apiError) setApiError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setApiError(null);

    const phoneE164 = phoneRef.current?.getE164Number() ?? "";

    const payload: {
      description: string;
      email?: string;
      phone?: string;
      user_name?: string;
    } = {
      description: [
        `Deletion request type: ${requestTypeLabel(form.requestType)}`,
        "",
        form.reason.trim(),
      ].join("\n"),
    };

    if (form.email.trim()) payload.email = form.email.trim();
    if (phoneE164) payload.phone = phoneE164;
    if (form.username.trim()) payload.user_name = form.username.trim();

    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      let data: DeletionApiResponse | null = null;
      try {
        data = (await res.json()) as DeletionApiResponse;
      } catch {
        data = null;
      }

      if (!res.ok || data?.success === false) {
        const msg =
          data?.message ??
          (res.status === 429
            ? "Too many requests. Please wait and try again in an hour."
            : `Something went wrong (${res.status}). Please try again.`);
        throw new Error(msg);
      }

      setSuccessMessage(
        data?.message ??
          "Your data deletion request has been submitted successfully."
      );
      setSubmitted(true);
      setForm(EMPTY);
      phoneRef.current?.clear();
      setErrors({});
      setIdentifierError(null);
      setPhoneError(null);
      setHasPhoneValue(false);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Something went wrong. Please try again.";
      setApiError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {submitted ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="flex flex-col items-center justify-center text-center py-16 px-8 rounded-2xl border border-neutral-100 bg-neutral-50"
        >
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mb-5"
            style={{ backgroundColor: "rgba(43,166,230,0.1)" }}
          >
            <CheckCircle2 className="w-8 h-8" style={{ color: ACCENT }} />
          </div>
          <h2 className="text-xl font-bold text-neutral-900 mb-2">Request Submitted</h2>
          <p className="text-neutral-600 mb-6 max-w-md">
            {successMessage ??
              "Your data deletion request has been submitted successfully. The RealtorsIn team will review it and contact you using the details you provided."}
          </p>
          <button
            type="button"
            onClick={() => {
              setSubmitted(false);
              setSuccessMessage(null);
            }}
            className="text-sm font-semibold transition-colors"
            style={{ color: ACCENT }}
          >
            Submit another request
          </button>
        </motion.div>
      ) : (
        <form
          onSubmit={handleSubmit}
          noValidate
          className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-6 md:p-8 grid grid-cols-1 lg:grid-cols-12 gap-x-5 gap-y-5"
        >
          {/* Section: account details */}
          <div className="lg:col-span-8">
            <h2 className="text-base font-bold text-neutral-900">Your account details</h2>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <p className="text-xs text-neutral-500 leading-relaxed">
              At least one of user name, phone, or email is required
            </p>
          </div>

          {identifierError && (
            <p className="lg:col-span-12 text-xs text-red-500 -mt-2">{identifierError}</p>
          )}

          <div className="lg:col-span-4 flex flex-col">
            <label
              htmlFor="acr-username"
              className="block text-xs font-semibold text-neutral-700 mb-1.5 uppercase tracking-wide"
            >
              User name
            </label>
            <input
              id="acr-username"
              name="username"
              type="text"
              placeholder="Your RealtorsIn user name"
              value={form.username}
              onChange={handleChange}
              className={inputClass(!!identifierError && !form.username.trim())}
              autoComplete="username"
            />
          </div>

          <div className="lg:col-span-4 flex flex-col">
            <label
              htmlFor="floating_lead_phone"
              className="block text-xs font-semibold text-neutral-700 mb-1.5 uppercase tracking-wide"
            >
              Phone number
            </label>
            <ContactPhoneIti
              ref={phoneRef}
              hasError={!!phoneError || (!!identifierError && !hasPhoneValue)}
              onChange={() => {
                const digits = phoneRef.current?.getMobileDigits() ?? "";
                setHasPhoneValue(digits.length > 0);
                if (phoneError) setPhoneError(null);
                if (identifierError) setIdentifierError(null);
                if (apiError) setApiError(null);
              }}
            />
            {phoneError && <p className="text-xs text-red-500 mt-1 min-h-[1.25rem]">{phoneError}</p>}
          </div>

          <div className="lg:col-span-4 flex flex-col">
            <label
              htmlFor="acr-email"
              className="block text-xs font-semibold text-neutral-700 mb-1.5 uppercase tracking-wide"
            >
              Email address
            </label>
            <input
              id="acr-email"
              name="email"
              type="email"
              placeholder="you@example.com"
              value={form.email}
              onChange={handleChange}
              className={inputClass(!!errors.email || (!!identifierError && !form.email.trim()))}
              autoComplete="email"
            />
            {errors.email && <p className="text-xs text-red-500 mt-1 min-h-[1.25rem]">{errors.email}</p>}
          </div>

          {/* Two columns: delete (left) · reason + submit (right) */}
          <div className="lg:col-span-12 pt-5 mt-1 border-t border-neutral-100 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 lg:items-start">
            <div className="flex flex-col min-w-0">
              <h2 className="text-base font-bold text-neutral-900 mb-1">
                What would you like to delete?
              </h2>
              <p className="text-xs text-neutral-500 mb-3">Select one option</p>
              {errors.requestType && (
                <p className="text-xs text-red-500 mb-3">{errors.requestType}</p>
              )}
              <fieldset className="flex flex-col gap-3 border-0 p-0 m-0 min-w-0 flex-1">
                <legend className="sr-only">Deletion request type</legend>
                {REQUEST_OPTIONS.map((option) => {
                  const selected = form.requestType === option.value;
                  return (
                    <label
                      key={option.value}
                      className={`flex items-center gap-3 min-h-[48px] px-4 py-3 rounded-xl border cursor-pointer transition-all duration-200 ${
                        selected
                          ? "border-[#2BA6E6] bg-blue-50/50 ring-1 ring-[#2BA6E6]/30"
                          : "border-neutral-200 hover:border-neutral-300 bg-white"
                      }`}
                    >
                      <input
                        type="radio"
                        name="requestType"
                        value={option.value}
                        checked={selected}
                        onChange={() => {
                          setForm((prev) => ({
                            ...prev,
                            requestType: option.value,
                          }));
                          if (errors.requestType) {
                            setErrors((prev) => ({ ...prev, requestType: undefined }));
                          }
                          if (apiError) setApiError(null);
                        }}
                        className="w-4 h-4 accent-[#2BA6E6] flex-shrink-0"
                      />
                      <span className="text-sm font-semibold text-neutral-900 leading-snug">
                        {option.label}
                      </span>
                    </label>
                  );
                })}
              </fieldset>
            </div>

            <div className="flex flex-col min-w-0">
              <label
                htmlFor="acr-reason"
                className="block text-xs font-semibold text-neutral-700 mb-1.5 uppercase tracking-wide"
              >
                Reason for request *
              </label>
              <textarea
                id="acr-reason"
                name="reason"
                rows={3}
                placeholder="Describe why you are submitting this request. Include any relevant details…"
                value={form.reason}
                onChange={handleChange}
                className={`${inputClass(!!errors.reason)} resize-y w-full h-[8.25rem] min-h-[5rem] max-h-36`}
              />
              {errors.reason && <p className="text-xs text-red-500 mt-1">{errors.reason}</p>}
              {apiError && (
                <p className="text-sm text-red-600 font-medium mt-2">{apiError}</p>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full mt-3 inline-flex items-center justify-center gap-2 px-8 py-3 rounded-xl text-sm font-bold text-white transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed hover:opacity-95"
                style={{ backgroundColor: ACCENT }}
              >
                {loading ? (
                  <>
                    <span
                      className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"
                      aria-hidden
                    />
                    Submitting…
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Submit Request
                  </>
                )}
              </button>
            </div>
          </div>

          <p className="lg:col-span-12 text-xs text-neutral-400 text-center leading-relaxed pt-4 border-t border-neutral-100">
            By submitting this form, you confirm that the information provided is accurate. We
            may contact you to verify your identity before processing your request.
          </p>
        </form>
      )}
    </>
  );
}
